import { useEffect, useState } from 'react';
import NumberFlow from '@number-flow/react';

import { wholeDollars } from '@dale/plans';

import { formatMoneyCents } from '../lib/format';

const CENTS = { minimumFractionDigits: 2, maximumFractionDigits: 2 } as const;

/** Rolling digits need the Web Animations API; without it (tests, very old WebViews) the figure is plain text. */
const canRoll = typeof Element !== 'undefined' && typeof Element.prototype.animate === 'function';

/**
 * An amount that rolls digit by digit to its new value (NumberFlow), shown
 * exactly like formatMoneyCents (Figures.test.tsx holds them together).
 * NumberFlow names itself with the whole amount for screen readers.
 * `rollIn` counts up from zero once, for a moment like Cobrado.
 */
export function Money({ cents, rollIn = false, className }: { cents: number; rollIn?: boolean; className?: string }) {
  const shown = useRolledIn(cents, rollIn);
  return (
    <span className={className ? `figure ${className}` : 'figure'}>
      {canRoll ? <NumberFlow value={Math.abs(shown) / 100} prefix={shown < 0 ? '−$' : '$'} locales="en-US" format={CENTS} /> : formatMoneyCents(shown)}
    </span>
  );
}

const WHOLE = { maximumFractionDigits: 0 } as const;

/**
 * A plan price that rolls to its new value when the cycle changes: whole
 * dollars when it has no cents ("$50", "$1,440"), with the cents otherwise
 * ("$22.50"), since a negotiated discount carried to another plan can land on
 * cents and what a business pays is never rounded.
 */
export function Dollars({ cents, className }: { cents: number; className?: string }) {
  const whole = cents % 100 === 0;
  return (
    <span className={className ? `figure ${className}` : 'figure'}>
      {canRoll ? (
        <NumberFlow value={cents / 100} prefix="$" locales="en-US" format={whole ? WHOLE : CENTS} />
      ) : whole ? (
        wholeDollars(cents)
      ) : (
        formatMoneyCents(cents)
      )}
    </span>
  );
}

/** A count (bookings, clients, places) that rolls to its new value. */
export function Count({ value, rollIn = false, className }: { value: number; rollIn?: boolean; className?: string }) {
  const shown = useRolledIn(value, rollIn);
  return <span className={className ? `figure ${className}` : 'figure'}>{canRoll ? <NumberFlow value={shown} locales="en-US" /> : shown}</span>;
}

/** Zero on the first paint, then the real value, so NumberFlow rolls it in. */
function useRolledIn(value: number, rollIn: boolean): number {
  const [started, setStarted] = useState(!rollIn);
  useEffect(() => {
    if (started) return undefined;
    const frame = requestAnimationFrame(() => setStarted(true));
    return () => cancelAnimationFrame(frame);
  }, [started]);
  return started ? value : 0;
}
